/**
 * Sends a fetch request to the Shopify API.
 * Supports different HTTP methods and optional request payloads.
 * Returns JSON response data if successful or an error object otherwise.
 * @param {string} url - The Shopify API endpoint (relative to the root).
 * @param {string} [method='GET'] - HTTP method (GET, POST, PUT, DELETE).
 * @param {Object|null} [data=null] - Optional payload for POST/PUT requests.
 * @returns {Promise<Object>} - The response data or an error object.
 */
async function shopifyFetch(url, method = 'GET', data = null) {
  try {
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(`${window.Shopify.routes.root}${url}`, options);

    if (response.ok) {
      return await {
        status: response.ok,
        response: await response.json()
      }
    } else {
      return {
        ...response,
        sections: '',
      };
    }
  } catch (error) {
    return {
      ...error?.response,
      sections: '',
    };
  }
}

/**
 * Retrieves the current Shopify cart data.
 *
 * @returns {Promise<Object>} - The cart data from Shopify.
 */
export async function getCart() {
  return shopifyFetch('cart.js');
}

/**
 * Adds a product variant to the Shopify cart.
 * Sends a POST request to the Shopify cart API with the variant ID and quantity.
 * @param {number} variantId - The ID of the product variant to add.
 * @param {number} [quantity=1] - The quantity of the product to add (default is 1).
 * @returns {Promise<Object>} - The response from the Shopify API.
 */
export async function addToCart (variantId, quantity = 1) {
  const url = "cart/add.js";
  let data = null;

  if (Array.isArray(variantId)) {
    data = {
      items: variantId.map(id => ({
        id: id,
        quantity: 1
      }))
    };
  } else {
    data = {
      id: variantId,
      quantity,
    };
  }

  return shopifyFetch(url, 'POST', data);
}